import { completeTimesList, completeDaysList } from '../App';
import type { AvailabilityMap, TimeSlot } from '../types';
import type { DisplaySchedule } from './scheduleHelpers';

/** Klassen-Mitglied, wie es aus der API in die Merge-Logik kommt. */
interface MemberInput {
  member_id: string;
  name: string;
  user_id?: string | null;
  duration?: number | string | null;
  available_times?: AvailabilityMap | null;
  available_times_user_input?: AvailabilityMap | null;
  split_label?: string | null;
  is_locked?: boolean;
  [key: string]: unknown;
}

/** Ergebniszeile: Mitglied + Platzierungs-Zustand fürs Schedule-Grid (Table.js). */
export interface MergedStudent extends MemberInput {
  set_day: number | null;
  /** [start_index, end_index_exclusive] in completeTimesList, oder leer. */
  set_time_from_to: number[];
  is_set: boolean;
  is_not_assigned: boolean;
  is_locked: boolean;
  free_times: Record<number, TimeSlot[]> | null;
}

// Hilfsfunktion zum Formatieren der Zeiten (könnte auch hier stehen)
const formatAvailableTimesForDict = (
  availableTimes: AvailabilityMap | null | undefined,
): Record<number, TimeSlot[]> | null => {
    if (!availableTimes) return null;
    const formattedTimes: Record<number, TimeSlot[]> = {};
    Object.entries(availableTimes).forEach(([day, times]) => {
        if (times) formattedTimes[parseInt(day)] = times; // Verwende day index als key
    });
    return formattedTimes;
};

export const mergeStudentsWithSchedule = (
  studentsData: readonly MemberInput[] | null | undefined,
  schedule: DisplaySchedule | null | undefined,
): MergedStudent[] | Record<string, never> => {
    // Schedule: Erwartet Format { "TAGNAME": { "Zeit": "Student memberId", ... }, ... }
    // studentsData: Array von Member-Objekten [{ name: "...", member_id: ..., user_id: ..., available_times: {...}, duration: ... }, ...]

    console.log("mergeStudentsWithSchedule: studentsData:", studentsData);
    console.log("mergeStudentsWithSchedule: schedule:", schedule);

    if (!studentsData) {
        console.warn("mergeStudentsWithSchedule: studentsData ist leer oder undefined.");
        return {};
    }
    if (!schedule) {
        console.warn("mergeStudentsWithSchedule: schedule ist leer oder undefined.");
        // Erstelle Dict nur aus studentsData ohne Schedule-Infos
        const initialStudentsList: MergedStudent[] = [];
        studentsData.forEach(student => {
            initialStudentsList.push({
                ...student,
                is_set: false,
                set_day: null,
                set_time_from_to: [],
                free_times: formatAvailableTimesForDict(student.available_times),
                is_not_assigned: false, // Standardmäßig false
                is_locked: student.is_locked || false, // Behalte is_locked Status oder setze auf false
            });
        });
        return initialStudentsList;
    }


    // Erstelle ein Mapping von Studenten (voller Name) zu ihren Zeitslots aus dem Schedule
    const studentTimeSlots: Record<string, { day: number; times: number[] }> = {};
    const studentsInSchedule = new Set<string>(); // Set für schnellere Prüfung

    Object.entries(schedule).forEach(([dayName, timeSlots]) => {
        const dayIndex = completeDaysList.indexOf(dayName.toUpperCase());
        if (dayIndex === -1) {
            console.warn(`Unbekannter Tag im Schedule gefunden: ${dayName}`);
            return;
        }

        Object.entries(timeSlots).forEach(([time, studentMemberId]) => {
            if (!studentMemberId || studentMemberId === "") return; // Ignoriere leere Slots

            studentsInSchedule.add(studentMemberId); // Füge vollen Namen zum Set hinzu

            if (!studentTimeSlots[studentMemberId]) {
                studentTimeSlots[studentMemberId] = {
                    day: dayIndex, // Speichere Index
                    times: []
                };
            }
            const timeIndex = completeTimesList.indexOf(time);
            if (timeIndex !== -1) {
                studentTimeSlots[studentMemberId].times.push(timeIndex);
            } else {
                console.warn(`Unbekannte Zeit im Schedule gefunden: ${time} für ${studentMemberId}`);
            }
        });
    });

    // Erstelle das finale Dictionary
    const mergedStudentsList: MergedStudent[] = [];
    const studentsInData = new Set(studentsData.map(s => s.member_id));

    studentsData.forEach(student => {
        const studentMemberId = student.member_id;
        const studentName = student.split_label ? `${student.name} (${student.split_label})` : student.name;
        const timeSlots = studentTimeSlots[studentMemberId];

        let setTimeFromTo: number[] = [];
        if (timeSlots && timeSlots.times.length > 0) {
            // Sortiere Indizes, um Min/Max korrekt zu finden
            const sortedTimes = [...timeSlots.times].sort((a, b) => a - b);
            // set_time_from_to sollte [start_index, end_index_exclusive] sein
            // end_index_exclusive ist start_index + anzahl_slots
            // Anzahl Slots = duration / 15
            const durationInSlots = Math.max(1, Math.round(Number(student.duration || 30) / 15)); // Mind. 1 Slot
            const startIndex = sortedTimes[0];
            const endIndexExclusive = startIndex + durationInSlots;
            // Wir speichern [startIndex, endIndexExclusive] für Konsistenz
            setTimeFromTo = [startIndex, endIndexExclusive];

             // Validierung: Prüfen, ob alle erwarteten Zeiten vorhanden sind
             const expectedTimes: number[] = [];
             for(let i=0; i < durationInSlots; i++) {
                 expectedTimes.push(startIndex + i);
             }
             if(sortedTimes.length !== durationInSlots || !expectedTimes.every(et => sortedTimes.includes(et))) {
                 console.warn(`Inkonsistente Zeiten für ${studentName}(${studentMemberId}) im Schedule gefunden. Erwartet ${durationInSlots} Slots ab ${startIndex}, gefunden:`, sortedTimes);
             }

        }

        mergedStudentsList.push({
            ...student, // Alle Daten aus studentsData
            user_id: student.user_id,
            member_id: student.member_id,
            name: studentName, // Stelle sicher, dass der Name korrekt ist
            duration: student.duration || 30, // Fallback-Dauer
            set_day: timeSlots ? timeSlots.day : null,
            set_time_from_to: setTimeFromTo,
            is_set: Boolean(timeSlots && timeSlots.times.length > 0),
            is_not_assigned: false, // Standardmäßig false für Studenten aus der Klassenliste
            is_locked: student.is_locked || false, // Behalte is_locked Status oder setze auf false
            // Konvertiere available_times in das Format { dayIndex: [time1, time2, ...] }
            free_times: formatAvailableTimesForDict(student.available_times),
             available_times: student.available_times, // Behalte Original für Referenz
             available_times_user_input: student.available_times_user_input,
        });
    });

    // Füge fehlende Studierende aus dem Schedule zum Dictionary hinzu (als 'is_not_assigned')
    studentsInSchedule.forEach(studentMemberId => {
        if (!studentsInData.has(studentMemberId)) {


            console.warn(`Student "${studentMemberId}" im Schedule, aber nicht in Klassenliste gefunden.`);
            const timeSlots = studentTimeSlots[studentMemberId];
            let duration = 30; // Standarddauer
            let setTimeFromTo: number[] = [];

            if (timeSlots && timeSlots.times.length > 0) {
                 const sortedTimes = [...timeSlots.times].sort((a, b) => a - b);
                 const startIndex = sortedTimes[0];
                 // Versuche Dauer aus den Slots zu schätzen
                 const estimatedSlots = (sortedTimes[sortedTimes.length - 1] - startIndex) + 1;
                 duration = estimatedSlots * 15;
                 setTimeFromTo = [startIndex, startIndex + estimatedSlots]; // [start, end_exclusive]
            }


            mergedStudentsList.push({
                name: 'Entfernter Schüler',
                member_id: studentMemberId,
                user_id: null, // Unbekannt
                duration: duration,
                set_day: timeSlots ? timeSlots.day : null,
                set_time_from_to: setTimeFromTo,
                is_set: Boolean(timeSlots && timeSlots.times.length > 0),
                free_times: null, // Keine Zeiten bekannt
                is_not_assigned: true, // Markiere als nicht zugeordnet
                is_locked: false // Standardmäßig nicht gelockt für unbekannte Studenten
            });
        }
    });

    console.log("Merged studentsList:", mergedStudentsList);
    return mergedStudentsList;
};
// TODO: eventuell gibt es hier noch probleme mit den member_ids
